import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { type ReactNode, useContext, useState } from 'react';

import { UserContext } from '~/auth/userContext';
import { ElevatorUnitFields } from '~/components/compliance/detailPage/ElevatorUnitFields';
import PropertyEditCarDialog from '~/components/portfolio/PropertyEditCarDialog';
import type { Car } from '~/data/generated/model';
import type { CarDto } from '~/data/schemas/car';
import type { API_Compliance } from '~/data/schemas/compliance';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { BUTTON_SX, pageBandColor, pageBandDividerColor, SURFACE_RADIUS } from '~/theme';
import { canAccess, EDIT_ELEVATOR } from '~/utils/role';

interface ElevatorDetailPageHeaderProps {
  compliance: API_Compliance | null;
  car: Car | undefined;
  carLoading: boolean;
  onBack: (() => void) | null;
  backLabel?: string;
  onCarEdited: () => void;
  compact?: boolean;
  footer?: ReactNode;
}

export function ElevatorDetailPageHeader({
  compliance,
  car,
  carLoading,
  onBack,
  backLabel,
  onCarEdited,
  compact = false,
  footer,
}: ElevatorDetailPageHeaderProps) {
  const terms = useLocaleTerms();
  const [editOpen, setEditOpen] = useState(false);
  const {
    userDetails: { role: userRole },
  } = useContext(UserContext);
  const canEdit = canAccess(userRole, EDIT_ELEVATOR);

  const openViolations = compliance?.openViolations ?? 0;

  return (
    <Box
      px={compact ? 2 : { xs: 2, md: 3 }}
      py={compact ? 2 : { xs: 2, md: 3 }}
      sx={
        compact
          ? { bgcolor: pageBandColor }
          : {
              bgcolor: pageBandColor,
              borderBottom: 1,
              borderColor: 'divider',
              borderTopLeftRadius: SURFACE_RADIUS,
              borderTopRightRadius: SURFACE_RADIUS,
            }
      }
    >
      <Box display="flex" alignItems="flex-start" gap={3}>
        {onBack ? (
          <Box flexShrink={0} ml={-1}>
            <Tooltip title={backLabel ?? ''}>
              <IconButton onClick={onBack} aria-label={backLabel ?? 'Back'} size="small">
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

        <Box flex={1} minWidth={0}>
          <Stack gap="3px" alignItems="flex-start">
            <Box display="flex" flexDirection="row" alignItems="center" gap={1} flexWrap="wrap">
              {compliance ? (
                <>
                  {car?.criticalUnit ? (
                    <Chip size="small" color="warning" label={`Critical ${terms.unit}`} />
                  ) : null}
                  {compliance.inspectionStatus === '-' ? null : (
                    <Chip
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                      label={compliance.inspectionStatus}
                      color={
                        compliance.inspectionStatus.toLowerCase() === 'passed' ? 'success' : 'error'
                      }
                    />
                  )}
                  <Chip
                    size="small"
                    color={openViolations > 0 ? 'error' : 'default'}
                    variant={openViolations > 0 ? 'filled' : 'outlined'}
                    label={
                      openViolations > 0
                        ? `${openViolations} open ${(openViolations === 1 ? terms.violation : terms.violations).toLowerCase()}`
                        : `No open ${terms.violations.toLowerCase()}`
                    }
                  />
                </>
              ) : (
                <Skeleton width={180} animation="wave" />
              )}
            </Box>

            <Typography variant="h5" noWrap>
              {compliance ? (
                compliance.unitName
              ) : (
                <Skeleton width={220} sx={{ fontSize: '1.5rem' }} animation="wave" />
              )}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {compliance ? (
                [
                  compliance.portfolio,
                  compliance.property,
                  compliance.ahjId ? `License ${compliance.ahjId}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              ) : (
                <Skeleton width={280} />
              )}
            </Typography>

            <Box display="flex" alignItems="center" gap={0.5} ml={-0.5} mt={0.5}>
              {canEdit ? (
                <Button
                  size="small"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => setEditOpen(true)}
                  disabled={!car}
                  sx={BUTTON_SX}
                >
                  Edit
                </Button>
              ) : null}
            </Box>

            <Divider flexItem sx={{ mt: 1, borderColor: pageBandDividerColor }} />
          </Stack>

          <Box mt={compact ? 2 : { xs: 2, md: 3 }}>
            <ElevatorUnitFields car={car} loading={carLoading} />
          </Box>
          {footer}
        </Box>
      </Box>

      {/* Mounted only while open, and only for a role that can edit. The `CarDto` cast is
          the loophole `PropertyCarForm.tsx`'s `_PinCarDtoFieldsExistOnCar` comment
          describes (AMP-620). */}
      {editOpen && car && canEdit ? (
        <PropertyEditCarDialog
          open
          onClose={() => setEditOpen(false)}
          onSubmit={() => {
            setEditOpen(false);
            onCarEdited();
          }}
          defaultValues={car as unknown as CarDto}
        />
      ) : null}
    </Box>
  );
}
